'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'london' | 'houston' | 'shenzhen' | 'saudi';

interface ThemeContextType {
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Get default theme from environment variable, fallback to 'london'
  const defaultTheme: Theme =
    (process.env.NEXT_PUBLIC_DEFAULT_THEME as Theme) || 'london';

  const [theme] = useState<Theme>(defaultTheme);

  // Apply theme to document on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const value: ThemeContextType = {
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
